import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import BrowseLayout from '../../components/home/BrowseLayout';
import {
    LEGAL_EMAIL, EFFECTIVE_DATE,
    NOT_RECEIVED_DAYS, NOT_AS_DESCRIBED_DAYS, REFUND_BUSINESS_DAYS,
} from './legalInfo';
import './LegalPage.css';
import './GuaranteePage.css';

const Mail = () => <a href={`mailto:${LEGAL_EMAIL}`}>{LEGAL_EMAIL}</a>;

const icon = (children) => (
    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {children}
    </svg>
);

const PROMISES = [
    {
        title: 'Authentic and PSA Verified',
        body: 'Every card is graded by PSA. Each listing shows the PSA cert number, so you can look it up on PSA’s site before you buy.',
        icon: icon(<><path d="M12 3l7 3v5c0 4.5-3 8.3-7 10-4-1.7-7-5.5-7-10V6l7-3z" /><polyline points="8.5 12 11 14.5 15.5 9.5" /></>),
    },
    {
        title: 'The Exact Card Pictured',
        body: 'Photos are of the actual graded card you’re buying, front and back. The card you receive carries the cert number on your listing.',
        icon: icon(<><rect x="3" y="6" width="18" height="14" rx="2" /><circle cx="12" cy="13" r="3.5" /><path d="M8 6l1.5-2h5L16 6" /></>),
    },
    {
        title: 'Delivered or Refunded',
        body: 'Every order ships with tracking. If your card doesn’t arrive, you get a full refund.',
        icon: icon(<><path d="M3 7h11v9H3z" /><path d="M14 10h4l3 3v3h-7" /><circle cx="7" cy="17.5" r="1.8" /><circle cx="17" cy="17.5" r="1.8" /></>),
    },
    {
        title: 'As Described or Refunded',
        body: 'If the card isn’t what the listing showed, we cover the return shipping and refund you in full.',
        icon: icon(<><path d="M4 12a8 8 0 0 1 13.7-5.7L20 8.5" /><polyline points="20 4 20 8.5 15.5 8.5" /><path d="M20 12a8 8 0 0 1-13.7 5.7L4 15.5" /><polyline points="4 20 4 15.5 8.5 15.5" /></>),
    },
];

// Buyer Trust Guarantee: the plain-language version of the refund policy in
// the User Agreement (section 6). Keep the two in sync; claim windows come
// from legalInfo.js so they can't drift.
const GuaranteePage = () => {
    useEffect(() => { window.scrollTo(0, 0); }, []);

    return (
        <BrowseLayout>
            <section className="gt-hero">
                <h1>Buyer Trust Guarantee</h1>
                <p>Every card is PSA graded, photographed and backed by our guarantee. Shop with confidence.</p>
            </section>

            <ul className="gt-promises">
                {PROMISES.map((p) => (
                    <li key={p.title} className="gt-promise">
                        <span className="gt-promise-icon">{p.icon}</span>
                        <h2>{p.title}</h2>
                        <p>{p.body}</p>
                    </li>
                ))}
            </ul>

            <article className="legal-doc gt-faq">
                <h2>What if my card doesn’t arrive?</h2>
                <p>
                    Email <Mail /> with your order number within {NOT_RECEIVED_DAYS} days of your order date. We’ll
                    track it down with the carrier. If the card was lost or never delivered to your shipping address,
                    we’ll refund you in full.
                </p>

                <h2>What if my card isn’t as described?</h2>
                <p>
                    A card is not as described if it has a different cert number, card or grade than the listing, or
                    damage to the PSA case that isn’t shown in the listing photos. Email <Mail /> within{' '}
                    {NOT_AS_DESCRIBED_DAYS} days of delivery with your order number and clear photos of the card, its
                    label and its case, and keep everything as it arrived.
                </p>
                <p>
                    Once we confirm the problem, we’ll send you a prepaid shipping label. When the card is back with
                    us, we’ll refund you in full, including any shipping you paid.
                </p>

                <h2>How are refunds paid?</h2>
                <p>
                    Approved refunds go back to your original payment method through PayPal within{' '}
                    {REFUND_BUSINESS_DAYS} business days. Your bank or PayPal may take a little longer to show the
                    credit. PayPal’s own buyer protection also still applies to your purchase.
                </p>

                <h2>What isn’t covered?</h2>
                <ul>
                    <li><strong>Change of mind.</strong> All sales are final, so we can’t accept returns or exchanges otherwise.</li>
                    <li><strong>Disagreeing with the grade.</strong> Grades are PSA’s opinion. A different view of the grade, or a different result from another grading company, isn’t a defect.</li>
                    <li><strong>Market value.</strong> Card prices go up and down. PriceCharting figures on our site are estimates, not a promise of value.</li>
                    <li><strong>Address errors.</strong> Orders delivered to an address entered incorrectly at checkout, though we’ll always try to help.</li>
                </ul>

                <h2>Terms</h2>
                <p>
                    This guarantee is a plain-language summary of the refund policy in our{' '}
                    <Link to="/terms#refunds">User Agreement</Link>, which controls if the two ever differ. We may
                    update this guarantee from time to time; changes apply to orders placed after the update.
                    Effective {EFFECTIVE_DATE}.
                </p>
            </article>

            <section className="gt-cta">
                <h2>Ready to find your next card?</h2>
                <Link to="/pokemon" className="gt-cta-btn">Shop Pokémon</Link>
            </section>
        </BrowseLayout>
    );
};

export default GuaranteePage;
